import { useEffect, useRef, useState } from "react";
import { GroundWave } from "@/components/OrganicLines";

const FEATURES = [
  {
    category: "Horse records",
    stage: "Live",
    action: "keeps each horse's whole story",
    detail: "Identity, lineage, certificates, awards, media and registry history live in one record.",
  },
  {
    category: "Care & calendar",
    stage: "Live",
    action: "organizes every day of care",
    detail: "Vet, farrier, training, grooming, due dates and follow-up stay on one shared calendar.",
  },
  {
    category: "People & places",
    stage: "Live",
    action: "knows who is where",
    detail: "Owners, caretakers, barn access, current locations and movement history stay connected.",
  },
  {
    category: "Nutrition",
    stage: "Live",
    action: "runs the feed room",
    detail: "Inventory becomes horse-specific ration plans, feeding shifts and allergy-aware routines.",
  },
  {
    category: "BarnAssist & voice",
    stage: "Live",
    action: "answers from the aisle",
    detail: "Ask naturally, speak when your hands are busy, and listen back without leaving the work.",
  },
  {
    category: "Document import",
    stage: "Live",
    action: "turns files into working records",
    detail: "PDFs, spreadsheets and photos become linked facts while the original source stays attached.",
  },
  {
    category: "Financial control",
    stage: "Live",
    action: "finds the money that went missing",
    detail: "Track income, expenses, agreements and expected charges, then reconcile what actually arrived.",
  },
  {
    category: "Client billing",
    stage: "Next",
    action: "drafts the invoice you describe",
    detail: "You review and approve every invoice; Stripe sends it through your own connected account.",
  },
] as const;

const CYCLE_MS = 4200;
const SWAP_MS = 300;

type RelayFeature = (typeof FEATURES)[number];

function RelayCopy({
  feature,
  index,
  className = "",
}: {
  feature: RelayFeature;
  index: number;
  className?: string;
}) {
  return (
    <div className={`feature-relay-copy ${className}`}>
      <span className="flex items-center gap-3 font-mono text-xs font-bold text-carrot-ink">
        {String(index + 1).padStart(2, "0")} / {String(FEATURES.length).padStart(2, "0")}
        <span className="h-px w-8 bg-carrot/35" />
        {feature.stage}
      </span>
      <p className="mt-5 max-w-[17ch] text-[clamp(2.25rem,5vw,4.6rem)] font-extrabold leading-[0.94] tracking-[-0.045em] text-ink">
        It <span className="text-carrot-deep">{feature.action}</span>.
      </p>
      <p className="mt-6 max-w-xl text-base leading-relaxed text-ink/68 sm:text-lg">{feature.detail}</p>
    </div>
  );
}

/**
 * A text-only tour through the real product surface. The swap starts only in
 * view, pauses while someone is reading it, and becomes a static list under
 * reduced motion.
 */
export function FeatureRelay() {
  const rootRef = useRef<HTMLElement>(null);
  const swapTimerRef = useRef<number | undefined>(undefined);
  const [active, setActive] = useState(0);
  const [previous, setPrevious] = useState<number | null>(null);
  const [inView, setInView] = useState(false);
  const [paused, setPaused] = useState(false);
  const [pageVisible, setPageVisible] = useState(true);
  const [motionEnabled, setMotionEnabled] = useState(false);

  useEffect(() => {
    const media = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    setMotionEnabled(!media?.matches);

    const onMotionChange = (event: MediaQueryListEvent) => setMotionEnabled(!event.matches);
    media?.addEventListener?.("change", onMotionChange);

    return () => media?.removeEventListener?.("change", onMotionChange);
  }, []);

  useEffect(
    () => () => {
      if (swapTimerRef.current) window.clearTimeout(swapTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    const node = rootRef.current;
    if (!node || typeof IntersectionObserver === "undefined" || window.innerHeight === 0) {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (entry) setInView(entry.isIntersecting);
    }, {
      threshold: 0.35,
      rootMargin: "-8% 0px",
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onVisibility = () => setPageVisible(!document.hidden);
    onVisibility();
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  useEffect(() => {
    if (!inView || paused || !pageVisible || !motionEnabled) return;
    const timer = window.setTimeout(() => {
      setPrevious(active);
      setActive((active + 1) % FEATURES.length);
      if (swapTimerRef.current) window.clearTimeout(swapTimerRef.current);
      swapTimerRef.current = window.setTimeout(() => setPrevious(null), SWAP_MS);
    }, CYCLE_MS);
    return () => window.clearTimeout(timer);
  }, [active, inView, motionEnabled, pageVisible, paused]);

  const feature = FEATURES[active] ?? FEATURES[0]!;
  const previousFeature = previous === null ? null : (FEATURES[previous] ?? null);

  const chooseFeature = (index: number) => {
    if (swapTimerRef.current) window.clearTimeout(swapTimerRef.current);
    setPrevious(null);
    setActive(index);
  };

  return (
    <section
      id="capabilities"
      ref={rootRef}
      className="feature-relay"
      aria-labelledby="feature-relay-title"
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setPaused(false);
      }}
    >
      <GroundWave className="feature-relay-wave feature-relay-wave-top" />

      <div className="feature-relay-inner">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="feature-relay-body">
            <header className="feature-relay-heading">
              <p className="text-[0.66rem] font-bold uppercase tracking-[0.22em] text-carrot-ink">
                Inside the system
              </p>
              <h3 id="feature-relay-title" className="mt-4 text-3xl font-extrabold leading-[1.02] text-field-deep sm:text-4xl">
                One memory.<br />Many working parts.
              </h3>
              <p className="mt-4 max-w-sm leading-relaxed text-ink/68">
                Distinct systems behind Carrot, not eight names for the same AI chat. Client billing rolls out next.
              </p>
            </header>

            <div className="feature-relay-live">
              <div className="feature-relay-stage" aria-hidden="true">
                {previousFeature ? (
                  <RelayCopy
                    key={`previous-${previousFeature.category}`}
                    feature={previousFeature}
                    index={previous!}
                    className="feature-relay-copy-out"
                  />
                ) : null}
                <RelayCopy
                  key={feature.category}
                  feature={feature}
                  index={active}
                  className={previousFeature ? "feature-relay-copy-in" : ""}
                />
              </div>

              <svg aria-hidden viewBox="0 0 600 18" preserveAspectRatio="none" className="feature-relay-progress mt-7 h-4 w-full text-carrot">
                <path
                  d="M2 11 C90 3 164 16 252 8 S430 5 598 10"
                  pathLength={1}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                  className="feature-relay-progress-path opacity-75"
                />
              </svg>
            </div>
          </div>

          <div className="feature-relay-nav" role="group" aria-label="Product capabilities">
            {FEATURES.map((item, index) => (
              <button
                key={item.category}
                type="button"
                aria-pressed={index === active}
                aria-label={`${item.category}: ${item.detail}`}
                className="feature-relay-tab"
                onClick={() => chooseFeature(index)}
              >
                <span className="feature-relay-tab-number">{String(index + 1).padStart(2, "0")}</span>
                <span>{item.category}</span>
              </button>
            ))}
          </div>

          <ul className="feature-relay-static">
            {FEATURES.map((item, index) => (
              <li key={item.category}>
                <span className="font-mono text-xs font-bold text-carrot-ink">{String(index + 1).padStart(2, "0")}</span>
                <strong className="mt-2 block text-lg text-field-deep">{item.category}</strong>
                <span className="mt-1.5 block text-sm leading-relaxed text-ink/68">{item.detail}</span>
              </li>
            ))}
          </ul>

          <p className="feature-relay-supporting">
            <span>Also built in</span>
            Role-aware web and mobile access across barns, plus public horse stories, pedigrees and results on your own barn domain.
          </p>
        </div>
      </div>

      <GroundWave className="feature-relay-wave feature-relay-wave-bottom" />
    </section>
  );
}
